import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../css/DocumentHistoryResponsive.css";
import filterIcon from "../../../assets/Images/Filter.png";
import changeStatusIcon from "../../../assets/Images/ChangeStatus.png";
import deleteIcon from "../../../assets/Images/DeleteIcon.png";
import { formatDateToUserTimeZone } from "../../../utils/formatDate";
import trainDoc from "../../../assets/icons/TrainDoc.png";
import trainText from "../../../assets/icons/TrainText.png";
import trainUrls from "../../../assets/icons/TrainUrl.png";
import trainVideo from "../../../assets/icons/TrainVideo.png";
import trainQA from "../../../assets/icons/TrainQA.png";
import { getFilteredDocs } from "../../../utils/approvedContentTabs";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { formatFileType } from "../../../utils/FileType";
import downloadIcon from "../../../assets/AdminIcon 2/DownloadBlack.svg";
import viewIcon from "../../../assets/UserIcons/ViewIcon.svg";
import crossIcon from "../../../assets/icons/crossIcon.png";
import { useTableSort } from "../../../hooks/useTableSort";
import { SortableHeader } from "../../../components/SortableHeader";

const DocumentHistory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);
  const [uploadedText, setUploadedText] = useState([]);
  const [uploadedQA, setUploadedQA] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFileUuid, setSelectedFileUuid] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;

  const tabs = [
    { name: "Documents", path: "/admin/knowledge-repository" },
    { name: "Web URLs", path: "/admin/knowledge-repo-urls" },
    { name: "Video URLs", path: "/admin/knowledge-repo-video" },
    { name: "Texts", path: "/admin/knowledge-repo-text" },
    { name: "Q&A", path: "/admin/knowledge-repo-qa" },
  ];

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    fetchApprovedDocs();
    fetchTexts();
    fetchQAs();
  }, []);

  const fetchApprovedDocs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/get-enterprise-docs`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // ✅ Set all documents including pending
      setAllApprovedDocs(res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
    }
  };

  const fetchTexts = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/text`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUploadedText(res.data);
    } catch (error) {
      console.error("❌ Error fetching texts:", error);
    }
  };

  const fetchQAs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/qa`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUploadedQA(res.data);
    } catch (error) {
      console.error("❌ Error fetching QAs:", error);
    }
  };

  const handleRevokeAccess = async (fileId) => {
    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/revoke-docs`,
        {
          file_id: fileId,
          notes: "Admin revoked global access",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast(res.data.message || "Access revoked successfully");
      fetchApprovedDocs();
    } catch (error) {
      console.error("❌ Error revoking access:", error);
      showErrorToast(error.response?.data?.error || "Failed to revoke access");
    }
  };

  const handleDeleteFile = async (pythonFileUuid) => {
    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/delete`,
        { python_file_uuid: pythonFileUuid },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast(res.data.message || "File deleted successfully");
      fetchApprovedDocs();
    } catch (err) {
      console.error("❌ Error deleting file:", err);
      showErrorToast(err.response?.data?.message || "Failed to delete file");
    }
  };

  const handleDeleteDocument = async (fileId) => {
    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/docs/soft-delete/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast("Document deleted successfully");
      fetchApprovedDocs();
    } catch (error) {
      console.error("❌ Error deleting document:", error);
      showErrorToast("Failed to delete document");
    }
  };

  // ✅ New function: handle status change (approved / rejected / pending)
  const handleChangeStatus = async (fileId, newStatus) => {
    try {
      await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
        { file_id: fileId, approval_status: newStatus, notes: "" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast(`Status updated to ${newStatus}`);
      fetchApprovedDocs();
    } catch (err) {
      console.error("❌ Error updating status:", err);
      showErrorToast("Failed to update status");
    }
  };

  const documentFiles = getFilteredDocs(allApprovedDocs, "document");

  const filteredFiles = documentFiles
    .filter((file) =>
      file.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((file) => {
      if (!dayFilter) return true;
      const updatedDate = new Date(file.updatedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return updatedDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return updatedDate >= yesterday && updatedDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredFiles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredFiles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  // console.log("Current Data ", currentData);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleDownload = async (pythonFileUUID) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/download?python_file_uuid=${pythonFileUUID}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed: ${errText}`);
      }
      const blob = await response.blob();
      let filename = "downloaded_file";
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i,
        );
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast("File downloaded successfully.");
    } catch (error) {
      console.error("❌ Download failed:", error);
      showErrorToast("Failed to download file.");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // View (Open) File using Google Docs Viewer (opens Excel/Word/PPT online)
  const handleViewFile = (pythonFileUuid, originalFileName = "") => {
    const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${pythonFileUuid}`;

    // console.log("file url for the file", fileUrl);

    // ✅ Detect image by extension
    const imageExtensions = ["png", "jpg", "jpeg", "webp", "gif"];
    const extension = originalFileName.split(".").pop()?.toLowerCase();

    // console.log("Detected extension:", extension);

    if (imageExtensions.includes(extension)) {
      // console.log("🖼 Opening image directly");
      window.open(fileUrl, "_blank");
      return;
    }

    // console.log("📄 Opening via Google Docs Viewer");
    const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      fileUrl,
    )}&embedded=true`;

    window.open(googleViewUrl, "_blank");
  };

  // const handleViewFile = async (uuid) => {
  //   const response = await fetch(
  //     `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${uuid}`,
  //     { headers: { Authorization: `Bearer ${token}` } }
  //   );

  //   const blob = await response.blob();
  //   const url = URL.createObjectURL(blob);
  //   window.open(url, "_blank");
  // };

  const statsData = [
    {
      label: "Uploaded Documents",
      value: getFilteredDocs(allApprovedDocs, "document").length,
      icon: trainDoc,
    },
    {
      label: "Uploaded Web URLs",
      value: getFilteredDocs(allApprovedDocs, "webpage").length,
      icon: trainUrls,
    },
    {
      label: "Uploaded Video URLs",
      value: allApprovedDocs.filter((doc) => doc.type === "youtube_audio")
        .length,
      icon: trainVideo,
    },
    {
      label: "Uploaded Texts",
      value: uploadedText.length,
      icon: trainText,
    },
    {
      label: "Uploaded Q&A",
      value: uploadedQA.length,
      icon: trainQA,
    },
  ];

  const handleChangeFileStatus = async (python_file_uuid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/update_file_status/${python_file_uuid}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // ❌ No body sent → Backend toggles automatically
        },
      );

      const result = await response.json();

      if (!response.ok) {
        showErrorToast(result.message || "Failed to update file status");
        return;
      }

      showSuccessToast("Status updated successfully");

      // refresh file list
      fetchApprovedDocs();
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast("Something went wrong");
    }
  };
  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-1">
              {item.icon && (
                <img
                  className="icon-image"
                  src={item.icon}
                  alt={item.label}
                  style={{ width: "18px", height: "18px" }}
                />
              )}
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold ak-text-md">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>
            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold ak-text-lg">Uploaded Documents</span>
      </div>

      <div className="mt-3 mb-3 tabs-wrapper ">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              style={{
                fontWeight: currentTab === tab.path ? "700" : "500",
              }}
              className={`tab-button ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>
      {totalEntries === 0 ? (
        <></>
      ) : (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="ms-2 mt-2">Entries per page</label>
          </div>

          <div className="d-flex align-items-center">
            <div className="custom-search-wrapper me-3">
              <div
                className="input-group custom-search-group"
                style={{ width: "320px" }}
              >
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Documents"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <div className="">
              <button
                className="icon-button filter-dropdown-toggle action-button"
                type="button"
                data-bs-toggle="dropdown"
                title="Filter"
              >
                <img src={filterIcon} className="icon-image" alt="Filter" />
              </button>
              <div
                className="dropdown-menu profile-menu-dropdown p-2"
                style={{ minWidth: "200px" }}
              >
                <h6 className="fw-bolder refresh-prompts">Time</h6>
                <hr />
                {["asc", "desc"].map((order) => (
                  <div className="form-check" key={order}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sortOrder"
                      id={order}
                      value={order}
                      checked={sortOrder === order}
                      onChange={() => setSortOrder(order)}
                    />
                    <label className="form-check-label" htmlFor={order}>
                      {order === "asc" ? "Ascending" : "Descending"}
                    </label>
                  </div>
                ))}
                <hr />
                <h6 className="fw-bolderrefresh-prompts">By Day</h6>
                <hr />
                {["Today", "Yesterday", ""].map((day) => (
                  <div className="form-check" key={day || "all"}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="dayFilter"
                      id={day || "all"}
                      value={day}
                      checked={dayFilter === day}
                      onChange={() => setDayFilter(day)}
                    />
                    <label className="form-check-label" htmlFor={day || "all"}>
                      {day || "All"}
                    </label>
                  </div>
                ))}
                <hr />
                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    className="btn dropdown-btn bg-dark text-white btn-sm"
                    onClick={() => setCurrentPage(1)}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">
            No documents have been trained yet.
          </p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table knowledgeRepoDoc-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <SortableHeader
                    column="name"
                    label="Document Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="file_extension"
                    label="Document Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="size"
                    label="Document Size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="status"
                    label="Document Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="uploadedBy"
                    label="Uploaded By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="mode"
                    label="Mode"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Last Updated On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((file, index) => (
                  <tr key={file.id || index}>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                      title={file.name}
                    >
                      {file.name.replace(/\.[^/.]+$/, "")}
                    </td>
                    {/* <td
                      className="text-left"
                      style={{ cursor: "pointer", maxWidth: "120px" }}
                      onClick={() => navigate(`/admin/folder/${file.folderId}`)}
                    >
                      {file.folderName ? file.folderName : "N/A"}
                    </td> */}

                    <td data-label="Document Type" className="text-left">
                      {formatFileType(file.type, file.name)}
                    </td>
                    <td className="text-left">{formatFileSize(file.size)}</td>
                    <td className="text-left">
                      <select
                        className={`badge ${
                          file.status === "approved"
                            ? "bg-success"
                            : file.status === "pending"
                              ? "bg-warning"
                              : "bg-danger "
                        }`}
                        value={file.status}
                        onChange={(e) =>
                          handleChangeStatus(file.id, e.target.value)
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                    {/* <td className="text-left">
                      <span
                        className={`badge ${
                          file.file_status === "1"
                            ? "bg-success" // GREEN
                            : "bg-warning" // YELLOW
                        }`}
                      >
                        {file.file_status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td> */}
                    <td className="text-left">
                      {file.uploadedBy ? file.uploadedBy : "N/A"}
                    </td>
                    <td className="text-left">
                      {file.mode
                        ? file.mode.charAt(0).toUpperCase() + file.mode.slice(1)
                        : "N/A"}
                    </td>

                    <td className="text-left">
                      {formatDateToUserTimeZone(file.createdAt)}
                      {/* {new Date(file.createdAt).toLocaleString()} */}
                    </td>
                    <td className="text-left">
                      {formatDateToUserTimeZone(file.updatedAt)}
                      {/* {new Date(file.updatedAt).toLocaleString()} */}
                    </td>
                    <td className="text-left pointer">
                      <button
                        className="icon-button"
                        onClick={() =>
                          handleViewFile(file.pythonFileUuid, file.name)
                        }
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleDownload(file.pythonFileUuid)}
                        title="Download File"
                      >
                        <img
                          src={downloadIcon}
                          className="icon-image"
                          alt="Download File"
                        />
                      </button>

                      {/* <button
                        onClick={() =>
                          handleChangeFileStatus(file.pythonFileUuid)
                        }
                        className="icon-button"
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button> */}

                      {/* {(file.status === "approved" ||
                        file.status === "rejected") && (
                        <button
                          className="icon-button icon-image"
                          title="Revoke Access"
                          onClick={() => handleRevokeAccess(file.id)}
                        >
                          <img src={changeStatusIcon} alt="Revoke Access" />
                        </button>
                      )} */}
                      {/* <button
                        className="icon-button icon-image"
                        title="Delete"
                        onClick={() => {
                          setSelectedFileUuid(file.pythonFileUuid);
                          setShowDeleteModal(true);
                        }}
                      >
                        <img src={deleteIcon} alt="Delete" />
                      </button> */}

                      {showDeleteModal && (
                        <div
                          className="modal modal-backdrop-custom d-block"
                          tabIndex="-1"
                        >
                          <div className="modal-history modal-dialog-centered history-model">
                            <div className="modal-content position-relative">
                              {/* Close Button */}
                              <button
                                type="button"
                                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                                onClick={() => {
                                  if (!isDeleting) {
                                    setShowDeleteModal(false);
                                    setSelectedFileUuid(null);
                                  }
                                }}
                                aria-label="Close"
                              >
                                <img
                                  src={crossIcon}
                                  className="icon-image cross-i"
                                  alt="Close"
                                />
                              </button>

                              {/* Modal Body */}
                              <div className="text-center p-3">
                                <div
                                  className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    borderRadius: "50%",
                                    backgroundColor: "#f0f0f0",
                                    boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <img
                                    src={deleteIcon} // ← your train icon import
                                    alt="Training"
                                    style={{ width: "40px", height: "40px" }}
                                  />
                                </div>

                                <p className="fw-bold mt-3 mb-3 refresh-prompts text-wrap">
                                  Are you sure you want to delete this document
                                  permanently?
                                  <br />
                                </p>
                              </div>

                              {/* Modal Footer */}
                              <div className="modal-footer justify-content-center gap-2">
                                <button
                                  type="button"
                                  disabled={isDeleting}
                                  className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                                  onClick={() => {
                                    if (!isDeleting) {
                                      setShowDeleteModal(false);
                                      setSelectedFileUuid(null);
                                    }
                                  }}
                                >
                                  Cancel
                                </button>

                                <button
                                  type="button"
                                  disabled={isDeleting}
                                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                                  onClick={async () => {
                                    try {
                                      setIsDeleting(true);

                                      await handleDeleteDocument(
                                        selectedFileUuid,
                                      );

                                      // ✅ CLOSE THE MODAL AFTER SUCCESS
                                      setShowDeleteModal(false);
                                      setSelectedFileUuid(null);
                                    } catch (err) {
                                      showErrorToast(
                                        "Error inside modal delete handler",
                                      );
                                    } finally {
                                      setIsDeleting(false);
                                    }
                                  }}
                                >
                                  {isDeleting ? "Deleting..." : "Delete"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentHistory;
